import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { AuthService } from '../auth.service';
import { StorageKeyConstants } from '../../_constants/storage-key-constants';

export interface InactivityConfig {
  inactivityTimeout: number;
  warningTimeout: number;
}

const INACTIVITY_CONSTANTS = {
  DEFAULT_INACTIVITY_MINUTES: 30,
  DEFAULT_WARNING_MINUTES: 1,
  ACTIVITY_DEBOUNCE_MS: 500,
  MS_PER_MINUTE: 60 * 1000
};

const DEFAULT_CONFIG: InactivityConfig = {
  inactivityTimeout:
    INACTIVITY_CONSTANTS.DEFAULT_INACTIVITY_MINUTES *
    INACTIVITY_CONSTANTS.MS_PER_MINUTE,
  warningTimeout:
    INACTIVITY_CONSTANTS.DEFAULT_WARNING_MINUTES *
    INACTIVITY_CONSTANTS.MS_PER_MINUTE
};

@Injectable({
  providedIn: 'root'
})
export class InactivityService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly showWarning$ = new Subject<void>();
  private readonly sessionExtended$ = new Subject<void>();
  private readonly forceLogout$ = new Subject<void>();

  private inactivityTimer: Subscription | null = null;
  private warningTimer: Subscription | null = null;
  private activitySubscription: Subscription | null = null;

  private config: InactivityConfig = DEFAULT_CONFIG;
  private isMonitoring = false;
  private isWarningVisible = false;

  private readonly USER_ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click'
  ];

  constructor(
    private ngZone: NgZone,
    private authService: AuthService
  ) {}

  get onShowWarning$(): Subject<void> {
    return this.showWarning$;
  }

  get onSessionExtended$(): Subject<void> {
    return this.sessionExtended$;
  }

  get onForceLogout$(): Subject<void> {
    return this.forceLogout$;
  }

  /**
   * Start monitoring user activity for inactivity timeout
   */
  startMonitoring(config?: Partial<InactivityConfig>): void {
    if (this.isMonitoring) {
      return;
    }

    if (config) {
      this.config = { ...DEFAULT_CONFIG, ...config };
    }

    this.isMonitoring = true;
    this.setupActivityListener();
    this.startInactivityTimer();
  }

  /**
   * Stop monitoring user activity
   */
  stopMonitoring(): void {
    this.isMonitoring = false;
    this.isWarningVisible = false;
    this.clearTimers();
    this.clearActivityListener();
  }

  /**
   * Called when user confirms they are still active (clicks "Yes, Stay Signed In")
   */
  extendSession(): void {
    this.isWarningVisible = false;
    this.clearWarningTimer();
    this.resetInactivityTimer();
    this.sessionExtended$.next();
  }

  isWarningShown(): boolean {
    return this.isWarningVisible;
  }

  private performInactivityLogout(): void {
    this.stopMonitoring();
    this.forceLogout$.next();

    // Store the inactivity logout flag in session storage
    // This will be read by the login page to show the message
    sessionStorage.setItem(StorageKeyConstants.inactivityLogoutKey, 'true');

    // Perform logout
    this.authService.logOut();
  }

  private setupActivityListener(): void {
    this.clearActivityListener();

    // Run outside Angular zone for performance
    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = this.USER_ACTIVITY_EVENTS.map((eventName) =>
        fromEvent(document, eventName)
      );

      this.activitySubscription = merge(...activityEvents$)
        .pipe(
          debounceTime(INACTIVITY_CONSTANTS.ACTIVITY_DEBOUNCE_MS),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.ngZone.run(() => this.onUserActivity());
        });
    });
  }

  private clearActivityListener(): void {
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
      this.activitySubscription = null;
    }
  }

  private onUserActivity(): void {
    // Only reset timer if warning is not currently shown
    // If warning is shown, user must click the button to extend session
    if (!this.isWarningVisible && this.isMonitoring) {
      this.resetInactivityTimer();
    }
  }

  private startInactivityTimer(): void {
    this.clearInactivityTimer();

    this.inactivityTimer = timer(this.config.inactivityTimeout).subscribe(
      () => {
        this.showInactivityWarning();
      }
    );
  }

  private resetInactivityTimer(): void {
    this.startInactivityTimer();
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      this.inactivityTimer.unsubscribe();
      this.inactivityTimer = null;
    }
  }

  private showInactivityWarning(): void {
    this.isWarningVisible = true;
    this.showWarning$.next();
    this.startWarningTimer();
  }

  private startWarningTimer(): void {
    this.clearWarningTimer();

    this.warningTimer = timer(this.config.warningTimeout).subscribe(() => {
      this.performInactivityLogout();
    });
  }

  private clearWarningTimer(): void {
    if (this.warningTimer) {
      this.warningTimer.unsubscribe();
      this.warningTimer = null;
    }
  }

  private clearTimers(): void {
    this.clearInactivityTimer();
    this.clearWarningTimer();
  }

  ngOnDestroy(): void {
    this.stopMonitoring();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
